import { PlanLayout } from "@/components/plans/PlanLayout";
import { FileCheck, Award, CreditCard, Building2, Bookmark } from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { motion } from "framer-motion";
import { useTranslation } from 'react-i18next';

const KeieiKakushinPage = () => {
  const { t } = useTranslation();

  const benefits = [
    { Icon: Building2, titleKey: 'benefit1_title', descKey: 'benefit1_desc' },
    { Icon: CreditCard, titleKey: 'benefit2_title', descKey: 'benefit2_desc' },
    { Icon: Award, titleKey: 'benefit3_title', descKey: 'benefit3_desc' },
    { Icon: Bookmark, titleKey: 'benefit4_title', descKey: 'benefit4_desc' },
  ];

  const points = [
    { titleKey: 'point1_title', descKey: 'point1_desc' },
    { titleKey: 'point2_title', descKey: 'point2_desc' },
    { titleKey: 'point3_title', descKey: 'point3_desc' },
    { titleKey: 'point4_title', descKey: 'point4_desc' },
  ];

  return (
    <PlanLayout
      title={t('plans_detail.keiei_kakushin.title')}
      subtitle={t('plans_detail.keiei_kakushin.subtitle')}
      imageUrl="https://images.unsplash.com/photo-1520607162513-77705c0f0d4a"
    >
      <div className="space-y-8">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.overview_title')}</h2>
          <p className="text-white/70 leading-relaxed">{t('plans_detail.keiei_kakushin.overview_body')}</p>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.benefits_title')}</h2>
          <motion.div
            className="grid gap-4 md:grid-cols-2"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }}
          >
            {benefits.map(({ Icon, titleKey, descKey }) => (
              <motion.div
                key={titleKey}
                variants={{ hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }}
                whileHover={{ scale: 1.02 }}
                className="border border-white/10 bg-white/5 p-5 rounded-xl"
              >
                <div className="flex items-center mb-2 gap-2">
                  <Icon className="text-[#c9a84c]" size={20} />
                  <h3 className="font-bold text-white">{t(`plans_detail.keiei_kakushin.${titleKey}`)}</h3>
                </div>
                <p className="text-white/65 text-sm leading-relaxed">{t(`plans_detail.keiei_kakushin.${descKey}`)}</p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.docs_title')}</h2>
          <ul className="list-disc pl-5 space-y-2 text-white/70">
            <li>{t('plans_detail.keiei_kakushin.doc1')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc2')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc3')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc4')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc5')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc6')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc7')}</li>
            <li>{t('plans_detail.keiei_kakushin.doc8')}</li>
          </ul>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.flow_title')}</h2>
          <div className="border border-white/10 bg-white/5 p-6 rounded-xl">
            <ol className="list-decimal pl-5 space-y-3 text-white/70">
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step1_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step1_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step2_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step2_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step3_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step3_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step4_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step4_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step5_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step5_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step6_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step6_desc')}</li>
              <li><span className="font-bold text-white">{t('plans_detail.keiei_kakushin.flow_step7_label')}</span>：{t('plans_detail.keiei_kakushin.flow_step7_desc')}</li>
            </ol>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.points_title')}</h2>
          <div className="space-y-3">
            {points.map((item) => (
              <div key={item.titleKey} className="flex items-start gap-3">
                <FileCheck className="text-[#c9a84c] mt-1 flex-shrink-0" size={18} />
                <p className="text-white/70 leading-relaxed"><span className="font-bold text-white">{t(`plans_detail.keiei_kakushin.${item.titleKey}`)}</span>：{t(`plans_detail.keiei_kakushin.${item.descKey}`)}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="border border-[#c9a84c]/30 bg-[#c9a84c]/10 p-6 rounded-xl"
        >
          <h2 className="text-2xl font-bold mb-4 text-white">{t('plans_detail.keiei_kakushin.support_title')}</h2>
          <p className="mb-4 text-white/70">{t('plans_detail.keiei_kakushin.support_body')}</p>
          <ul className="list-disc pl-5 space-y-2 text-white/70 mb-4">
            <li>{t('plans_detail.keiei_kakushin.support_item1')}</li>
            <li>{t('plans_detail.keiei_kakushin.support_item2')}</li>
            <li>{t('plans_detail.keiei_kakushin.support_item3')}</li>
            <li>{t('plans_detail.keiei_kakushin.support_item4')}</li>
            <li>{t('plans_detail.keiei_kakushin.support_item5')}</li>
          </ul>
          <p className="mt-4 text-white/70">{t('plans_detail.keiei_kakushin.support_footer')}</p>
          <div className="mt-6">
            <h3 className="font-bold text-lg mb-3 text-white">{t('plans_detail.keiei_kakushin.contact_title')}</h3>
            <ContactForm
              buttonText={t('plans_detail.keiei_kakushin.contact_button')}
              className="border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10"
            />
          </div>
        </motion.section>
      </div>
    </PlanLayout>
  );
};

export default KeieiKakushinPage;
